import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    CircularProgress,
    Divider,
    Icon,
    InputAdornment,
    Popover,
    useTheme,
} from '../../../../../base';
import { IEmailTemplateLabel } from '../../../../../common';
import {
    CheckboxIcon,
    CheckboxCheckedIcon,
} from '../../../../../theme/overrides/CustomIcons';
import {
    StyledTrigger,
    StyledTriggerText,
    StyledPanel,
    StyledSearchField,
    StyledOptionsList,
    StyledOptionRow,
    StyledOptionLabel,
    StyledEmpty,
    StyledFooter,
    StyledApplyButton,
    StyledCancelButton,
} from './TemplatePicker.styles';

interface TemplatePickerProps {
    options: Array<IEmailTemplateLabel>;
    isLoading: boolean;
    isApplying: boolean;
    onApply: (templateId: number) => void;
}

const TemplatePicker = ({
    options,
    isLoading,
    isApplying,
    onApply,
}: TemplatePickerProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const triggerRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [appliedValue, setAppliedValue] = useState<string | null>(null);
    const [selectedValue, setSelectedValue] = useState<string | null>(null);

    const filteredOptions = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return options;
        return options.filter((option) =>
            option.label.toLowerCase().includes(term)
        );
    }, [options, search]);

    const appliedLabel = useMemo(
        () =>
            options.find((option) => option.value === appliedValue)?.label ??
            null,
        [options, appliedValue]
    );

    const handleOpen = () => {
        setSearch('');
        setSelectedValue(appliedValue);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSearch('');
        setSelectedValue(appliedValue);
    };

    const handleApply = () => {
        if (selectedValue === null) return;
        onApply(Number(selectedValue));
        setAppliedValue(selectedValue);
        setOpen(false);
        setSearch('');
    };

    return (
        <>
            <StyledTrigger
                ref={triggerRef}
                onClick={open ? handleClose : handleOpen}
                isopen={open ? 'true' : 'false'}
            >
                <StyledTriggerText>
                    {appliedLabel ?? t('jobApplicationPage.addTemplate')}
                </StyledTriggerText>
                {isApplying ? (
                    <CircularProgress size={16} color="secondary" />
                ) : (
                    <Icon
                        name="KeyboardArrowDownIcon"
                        size={20}
                        sx={{
                            color: theme.palette.secondary.main,
                            transition: 'transform 0.2s',
                            transform: open
                                ? 'rotate(180deg)'
                                : 'rotate(0deg)',
                        }}
                    />
                )}
            </StyledTrigger>

            <Popover
                open={open}
                anchorEl={triggerRef.current}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: theme.direction === 'rtl' ? 'right' : 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: theme.direction === 'rtl' ? 'right' : 'left',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            boxShadow: 'none',
                            border: 'none',
                            backgroundColor: 'transparent',
                            overflow: 'visible',
                        },
                    },
                }}
            >
                <StyledPanel>
                    <StyledSearchField
                        size="small"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder={t('jobApplicationPage.searchTemplate')}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Icon
                                        name="SearchIcon"
                                        size={16}
                                        sx={{ color: theme.palette.grey[500] }}
                                    />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <StyledOptionsList>
                        {isLoading ? (
                            <StyledEmpty>
                                <CircularProgress size={20} />
                            </StyledEmpty>
                        ) : filteredOptions.length === 0 ? (
                            <StyledEmpty>
                                <StyledOptionLabel variant="body2">
                                    {t('jobApplicationPage.noTemplatesFound')}
                                </StyledOptionLabel>
                            </StyledEmpty>
                        ) : (
                            filteredOptions.map((option, index) => {
                                const isSelected =
                                    selectedValue === option.value;
                                return (
                                    <Box key={option.value}>
                                        <StyledOptionRow
                                            onClick={() =>
                                                setSelectedValue(
                                                    isSelected
                                                        ? null
                                                        : option.value
                                                )
                                            }
                                        >
                                            {isSelected ? (
                                                <CheckboxCheckedIcon
                                                    sx={{
                                                        fontSize: 18,
                                                        color: theme.palette
                                                            .secondary.main,
                                                    }}
                                                />
                                            ) : (
                                                <CheckboxIcon
                                                    sx={{
                                                        fontSize: 18,
                                                        color: theme.palette
                                                            .grey[300],
                                                    }}
                                                />
                                            )}
                                            <StyledOptionLabel variant="body2">
                                                {option.label}
                                            </StyledOptionLabel>
                                        </StyledOptionRow>
                                        {index < filteredOptions.length - 1 && (
                                            <Divider />
                                        )}
                                    </Box>
                                );
                            })
                        )}
                    </StyledOptionsList>

                    <StyledFooter>
                        <StyledApplyButton
                            type="button"
                            variant="contained"
                            disableElevation
                            disabled={selectedValue === null}
                            onClick={handleApply}
                        >
                            {t('jobApplicationPage.applyTemplate')}
                        </StyledApplyButton>
                        <StyledCancelButton
                            type="button"
                            variant="outlined"
                            onClick={handleClose}
                        >
                            {t('common.cancel')}
                        </StyledCancelButton>
                    </StyledFooter>
                </StyledPanel>
            </Popover>
        </>
    );
};

export default TemplatePicker;
